import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@tanium/react-confirm-modal';
import { useToastNotificationsControl } from '@tanium/react-toast-notifications-context';
import { AsyncStatus } from '../../hooks/useAsync';
import { useTaniumInstances } from '../../hooks/useTaniumInstances';

interface ModalProps {
  isOpen: boolean;
  instanceId: string;
  instanceName: string;
  onRequestClose: () => void;
}

export const RemoveFromListModal: React.FC<ModalProps> = ({ isOpen, instanceId, instanceName, onRequestClose }) => {
  const { t } = useTranslation();
  const { deleteLinkedInstance } = useTaniumInstances();
  const { showNotification } = useToastNotificationsControl();

  useEffect(() => {
    if (deleteLinkedInstance.status === AsyncStatus.Success) {
      onRequestClose();
      deleteLinkedInstance.reset();
      showNotification({
        type: 'success',
        contents: t('Successfully removed instanceName from the instance list', { instanceName }),
      });
    } else if (deleteLinkedInstance.status === AsyncStatus.Failure) {
      onRequestClose();
      deleteLinkedInstance.reset();
      showNotification({
        type: 'error',
        contents: t('Failed to remove instanceName from the instance list', { instanceName }),
      });
    }
  }, [onRequestClose, deleteLinkedInstance, showNotification, t, instanceName]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      header={t('Remove Deleted Instance')}
      confirmLabel={t('Remove')}
      cancelLabel={t('Cancel')}
      onConfirm={async () => {
        await deleteLinkedInstance.execute(instanceId);
      }}
      isConfirmDisabled={deleteLinkedInstance.status === AsyncStatus.Pending}
      onCancel={() => {
        onRequestClose();
        deleteLinkedInstance.reset();
      }}
    >
      <span>
        {t(
          'This will permanently remove instanceName from your instance list. This action cannot be undone.',
          { instanceName }
        )}
      </span>
    </ConfirmModal>
  );
};
